import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Alert } from 'app/models/dto/alert.model';
import { Deal, NewDeal } from 'app/models/dto/deal.model';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { DealsService } from 'app/services/deals/deals.service';
import { LenderApiService } from 'app/services/lender-api/lender-api.service';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { GetDealOffers, UpdateOffer } from 'app/store/offers/offer.actions';
import { forkJoin, Subject, throwError, of, combineLatest } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { CreateNewDealAlert } from '../deal-alerts/deal-alerts.actions';
import {
  ClearDealStore,
  CreateDeal,
  CreateDeals,
  DealOfferAdded,
  DowngradeDealStatus,
  FundDeal,
  GetDeals,
  GetLenderDealStatus,
  RefreshDealStatus,
  RunDealLoopRanking,
  SaveDealStatus,
  SubscribeToDealLoopRanking,
  SubscribeToDealStatus,
  UpdateDeal,
  UpdateDealDetails,
  UnacceptBorrowerChosenOffer,
} from './deal.actions';
import { GetBorrowerApplications } from '../application/application.actions';
import { LoanProduct } from 'app/models/dto/loan-product.model';
import { RejectedDealReasonService } from 'app/services/rejectedDeal-reason.service';
import { Injectable } from '@angular/core';
import { CreateNewBorrowerAlert } from 'app/store/borrower/alerts/borrower-alerts.actions';
import { compose, patch, updateItem } from '@ngxs/store/operators';
import { DealLoopRank } from 'ng2-app/models/deals/deal-loop-rank.model';
import { DealUtility } from 'ng2-app/util/deal/deal.utility';
import { AddStackableNotification } from 'ng2-app/store/stackable-notifications/StackableNotifications.actions';
import { OfferOptionsState } from 'app/store/offers/offer-options.state';
import { OfferState } from 'app/store/offers/offer.state';
import { ApprovalsState } from 'app/store/offers/approvals.state';
import { SnackbarQueueService } from 'app/services/snackbar-queue/snackbar-queue.service';
import { GetTasksForBorrower } from 'ng2-app/services/workflow-tasks/workflow-tasks.actions';
import { GetOfferOptions } from 'ng2-app/store/offers/offer-options.actions';

export class DealStateModel {
  borrowerId: number;
  deals: Deal[];
  loading: boolean;
  loopSubscribed: boolean;
  runningLoop: boolean;
  savingDeal: boolean;
}

@State<Partial<DealStateModel>>({
  name: 'Deals',
  defaults: {
    deals: [],
    loading: true,
  }
})

@Injectable()

export class DealState {

  @Selector()
  static deals(state: DealStateModel) {
    return state.deals;
  }

  @Selector()
  static activeDeals(state: DealStateModel) {
    return state.deals.filter(d => d.stage !== 'funded' && d.stage !== 'inactive');
  }

  @Selector()
  static fundedDeals(state: DealStateModel) {
    return state.deals.filter(d => d.stage === 'funded');
  }

  @Selector()
  static inactiveDeals(state: DealStateModel) {
    return state.deals.filter(d => d.stage === 'funded' || d.stage === 'inactive');
  }

  @Selector()
  static loading(state: DealStateModel) {
    return state.loading;
  }

  @Selector()
  static savingDeal(state: DealStateModel) {
    return state.savingDeal;
  }

  @Selector()
  static dealDateClosed(state: DealStateModel) {
    return (dealId: number) => state.deals.find(deal => deal.id === dealId)?.dateClosed;
  }

  @Selector()
  static runningLoop(state: DealStateModel) {
    return state.runningLoop;
  }

  destroyed$ = new Subject<boolean>();

  constructor(
    private dealsService: DealsService,
    private lenderApiService: LenderApiService,
    private store: Store,
    private eventBusService: EventBusService,
    private pusherService: PusherService,
    private rejectedDealReasonService: RejectedDealReasonService,
    private snackbarQueueService: SnackbarQueueService,
  ) { }

  @Action(GetDeals, { cancelUncompleted: true })
  getDeals({ getState, patchState }: StateContext<DealStateModel>, { borrowerId, refresh = false }: GetDeals) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId === prevBorrowerId && refresh === false) {
      return;
    }

    if (borrowerId !== prevBorrowerId) {
      // reset state
      patchState({
        borrowerId,
        deals: [],
        loading: true
      });
    } else {
      patchState({
        loading: true
      });
    }

    // load deal data
    this.store.dispatch(new GetDealOffers(borrowerId, refresh));
    this.store.dispatch(new GetBorrowerApplications(borrowerId));
    return this.dealsService.get(borrowerId).pipe(
      catchError(() => {
        this.store.dispatch(new CreateNewBorrowerAlert({
          level: 'error',
          message: 'Failed to load borrower deals. Please refresh and try again.',
          dismissDelay: 5000,
        }));
        return of();
      }),
      map(responseDeals => {
        const deals = responseDeals.map(deal => ({
            ...deal,
            supportedApiActions: this.lenderApiService.getSupportedApiActions(deal),
            termsValues: DealUtility.getHighestTermsValues(deal),
            lastApplicationDate: DealUtility.getLastApplicationDate(deal),
          }));

        patchState({
          deals,
          loading: false
        });
      }));
  }

  @Action(CreateDeals)
  createDeals({ getState }: StateContext<DealStateModel>, { borrowerId, userId, loanProducts, rejectedReason }: CreateDeals) {
    const newDeals = loanProducts.map(loanProduct => this.loanProductToNewDeal(borrowerId, userId, loanProduct));
    const createDeal = newDeal => this.store.dispatch(new CreateDeal(newDeal));
    const head = newDeals[0];
    const tail = newDeals.slice(1);

    if (newDeals.length === 1) {
      return createDeal(head).pipe(
        tap(() => {
          this.saveRejectedReasons(loanProducts, getState().deals, rejectedReason);
        }),
        catchError(() => of())
      );
    }

    return createDeal(head).pipe(
      switchMap(() => forkJoin([
          ...tail.map(newDeal => createDeal(newDeal))
        ])),
      tap(() => {
        this.saveRejectedReasons(loanProducts, getState().deals, rejectedReason);
      }),
      catchError(() => of())
    );
  }

  @Action(CreateDeal)
  createDeal({ getState, patchState }: StateContext<DealStateModel>, { newDeal }: CreateDeal) {
    return this.dealsService.create(newDeal).pipe(
      catchError(err => throwError(() => err)),
      tap(deal => {
        // If this is a decisioned deal, prompt the user
        if (deal?.isDecisioning) {
          const message = deal?.message || 'Successfully queued deal for decisioning.';
          this.snackbarQueueService.addNotification(
            `${message}.`,
              'lendio-alert--success'
          );
        // If not, run the normal operation
        } else {
          deal.supportedApiActions = this.lenderApiService.getSupportedApiActions(deal);
          patchState({ deals: [deal, ...getState().deals] });
          this.eventBusService.publish(new this.eventBusService.types.GoToActiveTabEvent);
        }
      })
    );
  }

  @Action(UpdateDeal)
  updateDeal({ getState, patchState }: StateContext<DealStateModel>, { dealId, dealUpdates }: UpdateDeal) {
    return this.dealsService.update(dealId, dealUpdates).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(updatedDeal => {
      const dealsStateCopy = [...getState().deals];
      const dealIndex = dealsStateCopy.findIndex(d => d.id === dealId);
      dealsStateCopy[dealIndex] = { ...dealsStateCopy[dealIndex], ...updatedDeal }; // merge new deal data w/ previous to not lose values
      patchState({ deals: dealsStateCopy });
    }));
  }

  @Action(UpdateDealDetails)
  UpdateDealDetails(
    ctx: StateContext<DealStateModel>,
    { dealId, dealUpdates, ownershipChange }: UpdateDealDetails
  ) {
    ctx.patchState({savingDeal: true});
    return this.dealsService.update(dealId, dealUpdates).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      ctx.patchState({savingDeal: false});
      ownershipChange && this.eventBusService.publish( new this.eventBusService.types.AssignmentChangeCompletedEvent() );
      return of();
    }), tap(updatedDeal => {
      ctx.setState(patch({
        deals: updateItem( deal => deal.id === dealId, patch(updatedDeal)),
        savingDeal: false,
      }));
      ownershipChange && this.eventBusService.publish( new this.eventBusService.types.AssignmentChangeCompletedEvent() );
      ownershipChange && this.store.dispatch( new AddStackableNotification({
        title: 'Success',
        subTitle: 'Deal Owner changed successfuly.',
        badge: {
          type: 'icon',
          value: 'check',
          class: '!tw-bg-lendio-lime-500',
        }
      }));
    }));
  }

  @Action(FundDeal)
  fundDeal({ }: StateContext<DealStateModel>, { offer, deal }: FundDeal) {
    return this.store.dispatch(new UpdateOffer(offer.id, offer)).pipe(catchError(err => throwError(() => err)), tap(() => {
      deal.status = 'current';
      deal.acceptedOfferId = offer.id;
      this.store.dispatch(new UpdateDeal(deal.id, deal)).pipe(take(1), catchError(err => throwError(() => err))).subscribe(() => {
        this.eventBusService.publish(new this.eventBusService.types.GoToFundedTabEvent);
      });
    }));
  }

  @Action(ClearDealStore)
  clearDealStore(
    ctx: StateContext<DealStateModel>,
    { }: ClearDealStore
  ) {
    this.pusherService.unsubscribe(`deals-loop-updated-${ctx.getState().borrowerId}`);
    ctx.patchState({
      borrowerId: undefined,
      deals: [],
      loading: true,
      loopSubscribed: false,
    });
  }

  @Action(DealOfferAdded)
  DealOfferAdded(
    ctx: StateContext<DealStateModel>,
    { offer }: DealOfferAdded,
  ) {
    const byDealId = (deal => deal.id === offer.dealId);
    const currentDeal = { ...ctx.getState().deals?.find( byDealId ) as Deal };
    currentDeal?.offers?.push(offer);
    !!currentDeal && ctx.setState(patch({
      deals: updateItem(byDealId, patch({
        ...currentDeal,
        termsValues: DealUtility.getHighestTermsValues(currentDeal),
      })),
    }));
  }

  /**
   * save deal status state
   *
   * @param param0
   * @param param1
   */
	@Action(SaveDealStatus)
  saveDealStatus(
    { getState, patchState }: StateContext<DealStateModel>,
    { dealId, stage, status, reason, otherReason, subReasons }: SaveDealStatus) {
    return this.dealsService.update(dealId, { stage, status, reason, otherReason, subReasons }).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal\'s status. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(updatedDeal => {
      const modifiedState = getState().deals.map(deal => {
        if (deal.id === dealId) {
          return { ...deal, ...updatedDeal };
        }
        return deal;
      });
      patchState({ deals: modifiedState });
    }));
  }

  @Action(RefreshDealStatus)
  refreshDealStatus({ getState, patchState }: StateContext<DealStateModel>, { dealId }: RefreshDealStatus) {
    return this.dealsService.getDealById(dealId).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(refreshedDeal => {
      const { status, stage, statusOptions, last_inactive_status, dead_disposition_deals } = refreshedDeal;
      const updatedState = getState().deals.map(deal => {
        if (deal.id === dealId) {
          deal.statusOptions = statusOptions;
          deal.stage = stage;
          deal.status = status;
          deal.dead_disposition_deals = dead_disposition_deals;
          deal.last_inactive_status = last_inactive_status;
        }
        return deal;
      });
      patchState({ deals: updatedState });
    }));
  }

  @Action(GetLenderDealStatus)
  getLenderDealStatus({ getState, patchState }: StateContext<DealStateModel>, { deal }: GetLenderDealStatus) {
    //Only dispatch if On Deck & there is a lenderCustomerId
    if (deal && deal.loanProductLenderId === 224 && !!deal.lenderCustomerId) {
      return this.lenderApiService.getDealStatus(deal.id).pipe(take(1)).subscribe({
        next: (res) => {
            if (res && res.contactStatus && res.contactStatus !== deal.lenderApiStatus) {
              deal.lenderApiStatus = res.contactStatus;
              deal.status = res.outcomeStatus;
              return deal;
            }
        },
        error: (res) => {
          console.warn('Unable to retrieve On Deck status: ' + res);
        }
      });
    }

    return deal;
  }

  @Action(SubscribeToDealStatus)
  subscribeToDealStatus({}: StateContext<DealStateModel>, { dealId }: SubscribeToDealStatus) {
    this.pusherSubscribe(dealId);
  }

  @Action(SubscribeToDealLoopRanking)
  SubscribeToDealLoopRanking(
    ctx: StateContext<DealStateModel>,
    { borrowerId }: SubscribeToDealLoopRanking
  ) {
    if(!ctx.getState().loopSubscribed) {
      ctx.patchState({loopSubscribed: true});
      this.pusherService
        .subscribe(`deals-loop-updated-${borrowerId}`)
        .bind_global((event, data) => {
          if(data?.borrowerId === ctx.getState().borrowerId) {
            ctx.setState(patch(
              this.getDealLoopRankingPatch(data.loopRanks, ctx.getState().deals)
            ));
          }
        });
    }
  }

  @Action(RunDealLoopRanking)
  RunDealLoopRanking(
    ctx: StateContext<DealStateModel>,
    { borrowerId }: RunDealLoopRanking,
  ) {
    ctx.patchState({runningLoop: true});
    return this.dealsService.runDealLoopRanking(borrowerId).pipe(
      catchError(err => {
        ctx.patchState({runningLoop: false});
        return throwError(err);
      }),
      tap( ranks => {
        ctx.setState(patch(
          this.getDealLoopRankingPatch(ranks, ctx.getState().deals)
        ));
      })
    );
  }

  @Action(UnacceptBorrowerChosenOffer)
  unacceptBorrowerChosenOffer(
    ctx: StateContext<DealStateModel>,
    { dealId } : UnacceptBorrowerChosenOffer,
  ) {
    return this.dealsService.unacceptChosenOffer(dealId).pipe(
      catchError(err => throwError(err)),
      tap(() => {
        const borrowerId = ctx.getState().borrowerId;
        this.store.dispatch(new GetDeals(borrowerId, true));
        this.store.dispatch(new GetTasksForBorrower(borrowerId));
        this.store.dispatch(new GetOfferOptions(borrowerId));
      })
    );
  }

  @Action(DowngradeDealStatus)
  DowngradeDealStatus(_: StateContext<DealStateModel>,
    { dealId }: DowngradeDealStatus) {
    const deal$ = this.store.select(DealState.deals).pipe(map((deals) => deals.find((deal) => deal.id === dealId)));
    const dealOptions$ = this.store.select(OfferOptionsState.dealOptions).pipe(map(filterFn => filterFn(dealId)));
    const dealOffers$ = this.store.select(OfferState.dealOffers).pipe(map(filterFn => filterFn(dealId, '')));
    const dealApprovals$ = this.store.select(ApprovalsState.forDeal).pipe(map(filterFn =>filterFn(dealId)));

    combineLatest([
      deal$,
      dealOptions$,
      dealOffers$,
      dealApprovals$,
    ]).pipe(take(1))
      .subscribe(([deal, options, offers, approvals]) => {
        if (deal?.stage !== 'underwriting' || deal?.status !== 'offerToBorrower') {
          return;
        }
        const hasPublishedOptions = (options || []).some((option) => (
          Boolean(option.publishedToBorrower) && !['draft', 'declined'].includes(option.status)
        ));
        const hasPublishedOffers = (offers || []).some((offer) => Boolean(offer.publishedToBorrower));
        const hasPublishedApprovals = (approvals || []).some((approval) => Boolean(approval.publishedToBorrower));

        if (hasPublishedOptions || hasPublishedOffers || hasPublishedApprovals) {
          return;
        }

        this.store.dispatch(new SaveDealStatus(deal.id, 'underwriting', 'offerReceived', '', [], []))
          .subscribe();
      });
  }

  getDealLoopRankingPatch(loopRanks: DealLoopRank[], currentDeals: Deal[]) {
    const updateDeals = currentDeals.map( deal => {
      const loopRank = loopRanks.find( rank => rank.dealId === deal.id)?.rank || 0;
      return updateItem<Deal>(
        sDeal => sDeal.id === deal.id,
        patch<Deal>({
          loopRank,
          lastLoopRankRun: Date.now(),
        })
      );
    });
    return {
      deals: compose(...updateDeals),
      runningLoop: false,
    };
  }

  pusherSubscribe(dealId: number) {
    this.pusherService
      .subscribe(`deal-${dealId}-status`)
      .bind_global(this.handlePusherEvent.bind(this, dealId));
  }

  handlePusherEvent(dealId: number, eventName: string) {
    if (eventName.startsWith('deal-status-')) {
      this.store.dispatch(new RefreshDealStatus(dealId));
    }
  }

  private loanProductToNewDeal(borrowerId: number, userId: number, loanProduct: LoanProduct): NewDeal {
    return {
      borrowerId,
      lenderUserId: userId,
      loanProductId: loanProduct.id,
      loanProductLenderId: loanProduct.lenderId,
      stage: 'application',
      status: 'appInProgress',
      type: null,
    };
  }

  private saveRejectedReasons(loanProducts: LoanProduct[], deals: Deal[], rejectedReason: { reason: string, comment: string } | null) {
    if (!rejectedReason) {
      return;
    }

    // build rejected reason for each deal on rejected loan product
    const rejectedReasons = loanProducts
      .filter(loanProduct => loanProduct.matchStatus === 'rejected')
      .map(loanProduct => deals.find(deal => deal.loanProductId === loanProduct.id))
      .filter(x => !!x)
      .map(deal => ({ reason: rejectedReason.reason, comment: rejectedReason.comment, dealId: (deal as Deal).id }));

    this.rejectedDealReasonService.post(rejectedReasons).subscribe(() => { });
  }
}
